import { setHttpClientErrorLogger } from '@/lib/api';

import { bindLauncherLoggerApiKey, launcherLogger } from './beaconLog';
import { extractApiKey, init } from './launcher';

// Capture `document.currentScript` SYNCHRONOUSLY at IIFE-execution time. It
// reads as `null` from any later callback (DOMContentLoaded, setTimeout,
// etc.), which would silently drop the apiKey from the customer's
// `<script>` tag and make the iframe URL fall back to the host page's
// `/embed/` instead of the embed origin's. Customer integration is always:
//
//   <script src="https://embed.example.com/launcher.js?apiKey=KEY" async></script>
//
// so this matters even for the common `async` case (where the script can
// execute before DOMContentLoaded if the document is still loading).
//
// `init()` is async (fetches /chat-bot/config before mounting the bubble);
// the promise is intentionally orphaned here — the IIFE wrapper has no
// teardown handle and any fetch failure is already handled inside init().
if (typeof document === 'undefined') {
  // Not in a browser context (e.g. SSR pre-render); nothing to do.
} else {
  const scriptEl = document.currentScript as HTMLScriptElement | null;
  // Extract the apiKey up-front so the shared httpClient error logger can
  // close over it. Binding the logger BEFORE init() means a very early
  // `/chat-bot/config` failure still lands in Datadog.
  const apiKey = extractApiKey(scriptEl?.src ?? '');
  bindLauncherLoggerApiKey(apiKey);
  setHttpClientErrorLogger((details) => {
    launcherLogger.error({
      event: { outcome: 'failure', type: 'chat_embed.http_request_failed' },
      message: 'httpClient request failed',
      requestId: details.requestId,
      context: {
        http: {
          method: details.method,
          request_id: details.requestId,
          status_code: details.status,
          status_text: details.statusText,
          url: details.path,
        },
      },
    });
  });
  const start = (): void => {
    init({ scriptEl }).catch((error: unknown) => {
      // eslint-disable-next-line no-console -- last-resort diagnostic if init itself throws
      console.error('[circuit-chat-embed] launcher init failed:', error);
    });
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
}
